import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  type SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AuthService,
  CompanyService,
  RecentSearch,
  ShortcutService,
} from '@app/core';
import {
  FILTER_FIELD_BY_KEY,
  FilterCondition,
  QueryContext,
  conditionKey,
  formatQuery,
  fromApiFilter,
  parseQuery,
  toApiFilter,
} from '@app/model';
import { Subscription, combineLatest, of } from 'rxjs';
import { catchError, startWith } from 'rxjs/operators';
import {
  QueryPreset,
  QuerySuggestion,
  chipValue,
  querySuggestions,
} from './query-suggestions';

/** A chip of the bar: a condition, or a token the parser could not read. */
export interface QueryChip {
  condition: FilterCondition | null;
  raw: string | null;
  /** i18n key of the field, or the property of a foreign condition. */
  label: string;
  value: string;
  /** Part of the definition of the dynamic list. */
  locked: boolean;
  /** i18n key of the reason a token is invalid. */
  reason: string | null;
}

/** The filter and the unread tokens of a search started in the bar. */
export interface QueryChange {
  filter: unknown[];
  invalid: string[];
}

let nextId = 0;

/**
 * The query bar of the search and of a list: the filter as chips and a field
 * for free text and tokens such as `sector:Payments employees:50..500`. Enter
 * or Tab turns the text into chips; a click on a chip puts it back into the
 * field for editing. Tokens it cannot read stay as red chips and are not sent.
 *
 * The chips are a draft: they change at once (also from the filter builder,
 * `setDraft`), the search runs on Enter in the empty field, Ctrl/⌘+Enter or
 * the Search button (`search`), which is highlighted while the chips differ
 * from the filter of the page.
 */
@Component({
  selector: 'app-query-bar',
  templateUrl: './query-bar.component.html',
  styleUrls: ['./query-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QueryBarComponent implements OnChanges, OnDestroy {
  private changeDetector = inject(ChangeDetectorRef);
  private companyService = inject(CompanyService);
  private authService = inject(AuthService);
  private shortcuts = inject(ShortcutService);

  /** The filter the page searched with, in the format of the API. */
  @Input() filter: unknown[] | null = null;
  @Input() filterEnabled: boolean | null = true;
  /** Tokens of the URL the parser could not read. */
  @Input() invalid: string[] | null = null;
  /** The definition of a dynamic list: its chips carry a lock. */
  @Input() lockedFilter: unknown[] | null = null;
  @Input() presets: QueryPreset[] = [];
  @Input() recent: RecentSearch[] = [];
  /** Whether the filter builder below the bar is open. */
  @Input() builderOpen = false;
  @Input() builderId: string | null = null;
  /** A search with the chips: the filter and the unread tokens. */
  @Output() queryChange = new EventEmitter<QueryChange>();
  /** A search with the chips whose filter differs from the page's. */
  @Output() filterChange = new EventEmitter<unknown[]>();
  @Output() recentSelected = new EventEmitter<RecentSearch>();
  @Output() builderToggle = new EventEmitter<void>();

  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;

  public readonly id = `query-bar-${nextId++}`;
  public conditions: FilterCondition[] = [];
  public invalidTokens: string[] = [];
  public chips: QueryChip[] = [];
  /** The filter of the chips, for the filter builder. */
  public draftFilter: unknown[] = [];
  /** Whether the chips differ from the filter the page searched with. */
  public dirty = false;
  public text = '';
  public open = false;
  public suggestions: QuerySuggestion[] = [];
  public activeIndex = -1;
  /** The chip Backspace or the arrow keys marked, removed by Backspace. */
  public markedIndex: number | null = null;

  private context: QueryContext = {};
  private lockedKeys = new Set<string>();
  /** The filter and unread tokens of the page, serialized. */
  private applied = '';
  private subscription: Subscription;
  private unregister: (() => void)[];

  constructor() {
    this.subscription = combineLatest([
      this.companyService.subsectors$.pipe(catchError(() => of([]))),
      this.companyService.countryCodes$.pipe(catchError(() => of([]))),
      this.authService.username$.pipe(startWith(null)),
    ]).subscribe(([sectors, countries, userName]) => {
      this.context = {
        sectors: sectors.map((x) => ({ id: x.id, name: x.name })),
        countries,
        userName,
      };
      this.updateChips();
      this.changeDetector.markForCheck();
    });
    this.unregister = [
      this.shortcuts.register({
        keys: '/',
        priority: 10,
        handler: () => this.focus(),
      }),
      this.shortcuts.register({
        keys: 'f',
        priority: 10,
        handler: () => this.builderToggle.emit(),
      }),
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    // A new filter of the page (a search, another list, browser back)
    // replaces the draft.
    if (changes['filter']) {
      this.conditions = fromApiFilter(this.filter);
    }
    if (changes['invalid']) {
      this.invalidTokens = [...(this.invalid ?? [])];
    }
    if (changes['filter'] || changes['invalid']) {
      this.applied = this.serialize(this.conditions, this.invalidTokens);
      this.draftFilter = toApiFilter(this.conditions);
      this.dirty = false;
    }
    if (changes['lockedFilter']) {
      this.lockedKeys = new Set(
        fromApiFilter(this.lockedFilter).map(conditionKey)
      );
    }
    this.updateChips();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.unregister.forEach((x) => x());
  }

  focus() {
    this.input.nativeElement.focus();
  }

  get activeId() {
    return this.open && this.activeIndex >= 0
      ? `${this.id}-option-${this.activeIndex}`
      : null;
  }

  onFocus() {
    this.open = true;
    this.updateSuggestions();
  }

  /** Focus left the bar, e.g. by a click elsewhere. */
  onBlur() {
    this.open = false;
    this.markedIndex = null;
  }

  onInput(text: string) {
    this.text = text;
    this.open = true;
    this.markedIndex = null;
    this.updateSuggestions();
  }

  onKeydown(event: KeyboardEvent) {
    const empty = !this.text;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        this.open = true;
        const count = this.suggestions.length;
        if (count) {
          const step = event.key === 'ArrowDown' ? 1 : -1;
          this.activeIndex =
            (this.activeIndex + step + count + 1) % (count + 1);
          if (this.activeIndex === count) {
            this.activeIndex = -1;
          }
        }
        return;
      }
      case 'Enter':
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          this.search();
        } else if (empty && this.markedIndex !== null) {
          this.edit(this.markedIndex);
        } else if (this.open && this.activeIndex >= 0) {
          this.pick(this.suggestions[this.activeIndex]);
        } else if (empty) {
          this.search();
        } else {
          this.commit(this.text);
        }
        return;
      case 'Tab':
        if (!empty) {
          event.preventDefault();
          this.commit(this.text);
        }
        return;
      case 'Backspace':
        if (empty && this.chips.length) {
          event.preventDefault();
          if (this.markedIndex === null) {
            this.markedIndex = this.chips.length - 1;
          } else {
            this.remove(this.markedIndex);
          }
        }
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
        if (empty && this.chips.length) {
          event.preventDefault();
          const last = this.chips.length - 1;
          if (event.key === 'ArrowLeft') {
            this.markedIndex =
              this.markedIndex === null
                ? last
                : Math.max(0, this.markedIndex - 1);
          } else {
            this.markedIndex =
              this.markedIndex === null || this.markedIndex >= last
                ? null
                : this.markedIndex + 1;
          }
        }
        return;
      case 'Escape':
        if (this.open) {
          this.open = false;
          this.activeIndex = -1;
        } else {
          this.input.nativeElement.blur();
        }
        return;
      default:
        this.markedIndex = null;
    }
  }

  /** A line of the suggestions was chosen with Enter or a click. */
  pick(suggestion: QuerySuggestion | undefined) {
    if (!suggestion) {
      return;
    }
    switch (suggestion.kind) {
      case 'insert':
        this.text = suggestion.text;
        this.updateSuggestions();
        this.focus();
        return;
      case 'commit':
      case 'preset':
        this.commit(suggestion.query);
        return;
      case 'recent':
        this.open = false;
        this.recentSelected.emit(suggestion.search);
        return;
    }
  }

  /** Turns a text into chips; a field gets at most one condition. */
  commit(text: string) {
    if (!text.trim()) {
      return;
    }
    const parsed = parseQuery(text, this.context);
    const conditions = [...this.conditions];
    for (const condition of parsed.conditions) {
      const index = conditions.findIndex((x) => x.field === condition.field);
      if (index >= 0) {
        conditions[index] = condition;
      } else {
        conditions.push(condition);
      }
    }
    const invalid = [
      ...this.invalidTokens,
      ...parsed.errors
        .map((x) => x.raw)
        .filter((x) => !this.invalidTokens.includes(x)),
    ];
    this.text = '';
    this.activeIndex = -1;
    this.update(conditions, invalid);
    this.updateSuggestions();
  }

  /** Removes a chip with its × or Backspace. */
  remove(index: number) {
    const chip = this.chips[index];
    if (!chip) {
      return;
    }
    this.markedIndex = null;
    if (chip.condition) {
      this.update(
        this.conditions.filter((x) => x !== chip.condition),
        this.invalidTokens
      );
    } else {
      this.update(
        this.conditions,
        this.invalidTokens.filter((x) => x !== chip.raw)
      );
    }
    this.focus();
  }

  /** Puts a chip back into the field as text, to change it. */
  edit(index: number) {
    const chip = this.chips[index];
    if (!chip || !this.filterEnabled) {
      return;
    }
    const text = chip.condition
      ? formatQuery([chip.condition], this.context)
      : chip.raw;
    if (chip.condition && !text) {
      // A condition the grammar cannot write stays as it is.
      return;
    }
    this.remove(index);
    this.text = text ?? '';
    this.open = true;
    this.updateSuggestions();
    this.focus();
  }

  /** Removes all chips. */
  reset() {
    this.update([], []);
  }

  /** The filter builder changed: its filter becomes the chips. */
  setDraft(filter: unknown[] | null) {
    this.update(fromApiFilter(filter), this.invalidTokens);
    this.changeDetector.markForCheck();
  }

  /**
   * Searches with the chips, after turning typed text into chips. Nothing
   * happens while the chips equal the filter of the page.
   */
  search() {
    if (!this.filterEnabled) {
      return;
    }
    if (this.text.trim()) {
      this.commit(this.text);
    }
    if (!this.dirty) {
      return;
    }
    const filter = toApiFilter(this.conditions);
    const filterChanged =
      JSON.stringify(filter) !==
      JSON.stringify(toApiFilter(fromApiFilter(this.filter)));
    this.open = false;
    this.activeIndex = -1;
    // The page answers with its new filter; until then the chips count as
    // searched, also when only unread tokens changed.
    this.applied = this.serialize(this.conditions, this.invalidTokens);
    this.dirty = false;
    this.queryChange.emit({ filter, invalid: this.invalidTokens });
    if (filterChanged) {
      this.filterChange.emit(filter);
    }
  }

  private update(conditions: FilterCondition[], invalid: string[]) {
    this.conditions = conditions;
    this.invalidTokens = invalid;
    this.draftFilter = toApiFilter(conditions);
    this.dirty = this.serialize(conditions, invalid) !== this.applied;
    this.updateChips();
  }

  private serialize(conditions: FilterCondition[], invalid: string[]) {
    return JSON.stringify([toApiFilter(conditions), invalid]);
  }

  private updateChips() {
    this.chips = [
      ...this.conditions.map((condition): QueryChip => ({
        condition,
        raw: null,
        label: FILTER_FIELD_BY_KEY.has(condition.field)
          ? `filter.field.${condition.field}`
          : condition.field,
        value: chipValue(condition, this.context),
        locked: this.lockedKeys.has(conditionKey(condition)),
        reason: null,
      })),
      ...this.invalidTokens.map((raw): QueryChip => ({
        condition: null,
        raw,
        label: '',
        value: raw,
        locked: false,
        reason:
          parseQuery(raw, this.context).errors[0]?.reason ??
          'filter.error.value',
      })),
    ];
  }

  private updateSuggestions() {
    this.suggestions = querySuggestions(
      this.text,
      this.context,
      this.presets,
      this.recent
    );
    this.activeIndex = -1;
  }
}
